import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import "./Dashboard.css";
import "./AIResumeBuilder.css";
import Footer from "../Landing Page/Footer";
import Feedback from "./Feedback";
import { useSubscription } from "../SubscriptionContext";
import { useAuth } from "../AuthContext";
import { apiUrl } from "../config/apiBase";
import axios from "axios";
import ResumePreview from "./ResumePreview";

function AIResumeBuilder() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canGenerateQuestions, getUsageInfo, canCreateScratchResume, getResumeUsageInfo } = useSubscription();
  const { refreshTokenIfNeeded } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Always start with onboarding modal for fresh navigation
  const [showOnboarding, setShowOnboarding] = useState(true);
  
  // Check if coming from Job Tailored Resume with pre-filled data
  const preFilled = location.state?.preFilled || false;
  const tailoredResume = location.state?.tailoredResume || false;
  const jobAnalysis = location.state?.jobAnalysis || null;
  
  // Form data states
  const [personalInfo, setPersonalInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    location: "",
    linkedIn: "",
    portfolio: ""
  });

  const [professionalSummary, setProfessionalSummary] = useState("");
  const [animatedProfessionalSummary, setAnimatedProfessionalSummary] = useState("");
  // Animate text into textarea (typewriter effect)
  const animateSummary = (text) => {
    if (!text) {
      setAnimatedProfessionalSummary("");
      return;
    }
    let current = "";
    let i = 0;
    setAnimatedProfessionalSummary("");
    const interval = setInterval(() => {
      if (i < text.length) {
        current += text.charAt(i);
        setAnimatedProfessionalSummary(current);
        i++;
      } else {
        clearInterval(interval);
      }
    }, 18);
  };
  const [experience, setExperience] = useState([
    { type: 'work', company: "", position: "", startDate: "", endDate: "", current: false, description: "", projectName: "", techStack: "", projectLink: "" }
  ]);
  const [education, setEducation] = useState([
    { school: "", degree: "", field: "", graduationYear: "", startYear: "", gpa: "", location: "" }
  ]);
  const [skills, setSkills] = useState("");

  // Language selection state
  const [language, setLanguage] = useState("english");

  // Language skills state
  const [languageSkills, setLanguageSkills] = useState([
    { language: "", proficiency: "" }
  ]);

  // PDF generation states
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // AI assistance states
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFieldType, setAiFieldType] = useState(null);

  // Store original data for undo functionality
  const [originalPersonalInfo, setOriginalPersonalInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    location: "",
    linkedIn: "",
    portfolio: ""
  });
  const [originalProfessionalSummary, setOriginalProfessionalSummary] = useState("");
  const [originalExperience, setOriginalExperience] = useState([
    { type: 'work', company: "", position: "", startDate: "", endDate: "", current: false, description: "", projectName: "", techStack: "", projectLink: "" }
  ]);
  const [originalEducation, setOriginalEducation] = useState([
    { school: "", degree: "", field: "", graduationYear: "", startYear: "", gpa: "", location: "" }
  ]);
  const [originalSkills, setOriginalSkills] = useState("");

  // Prevent scrolling when onboarding is shown (only for from-scratch users)
  useEffect(() => {
    if (showOnboarding && !tailoredResume) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showOnboarding, tailoredResume]);

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo({ ...personalInfo, [field]: value });
  };

  const addExperience = () => {
    setExperience([...experience, { type: 'work', company: "", position: "", startDate: "", endDate: "", current: false, description: "", projectName: "", techStack: "", projectLink: "" }]);
  };

  const removeExperience = (index) => {
    const newExperience = experience.filter((_, i) => i !== index);
    setExperience(newExperience);
  };

  const updateExperience = (index, field, value) => {
    const newExperience = [...experience];
    newExperience[index][field] = value;
    setExperience(newExperience);
  };

  const addEducation = () => {
    setEducation([...education, { school: "", degree: "", field: "", graduationYear: "", startYear: "", gpa: "", location: "" }]);
  };

  const removeEducation = (index) => {
    const newEducation = education.filter((_, i) => i !== index);
    setEducation(newEducation);
  };

  const updateEducation = (index, field, value) => {
    const newEducation = [...education];
    newEducation[index][field] = value;
    setEducation(newEducation);
  };

  // Language skills functions
  const addLanguageSkill = () => {
    setLanguageSkills([...languageSkills, { language: "", proficiency: "" }]);
  };

  const removeLanguageSkill = (index) => {
    if (languageSkills.length > 1) {
      const newLanguageSkills = languageSkills.filter((_, i) => i !== index);
      setLanguageSkills(newLanguageSkills);
    }
  };

  const updateLanguageSkill = (index, field, value) => {
    const newLanguageSkills = [...languageSkills];
    newLanguageSkills[index][field] = value;
    setLanguageSkills(newLanguageSkills);
  };

  const generatePdfPreview = async () => {
    setPdfGenerating(true);
    
    try {
      const token = await refreshTokenIfNeeded();

      if (!token) {
        alert('Please sign in to continue.');
        setPdfGenerating(false);
        return;
      }

      const resumeData = {
        personalInfo,
        professionalSummary,
        experience: experience.filter(exp => exp.company && exp.position),
        education: education.filter(edu => edu.school && edu.degree),
        skills
      };
      
      // First save the resume
      let resumeId = location.state?.resumeId;
      
      if (resumeId) {
        // Update existing resume
        await axios.put(
          apiUrl('/api/resume-builder/update-step'),
          {
            resumeId: resumeId,
            step: 4,
            stepData: resumeData
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        );
      } else {
        // Create new resume
        const createResponse = await axios.post(
          apiUrl('/api/resume-builder/create-from-scratch'),
          resumeData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        );
        resumeId = createResponse.data.resumeId;
      }

      // Generate PDF
      const pdfResponse = await axios.post(
        apiUrl('/api/resume-builder/generate-pdf'),
        { resumeId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (pdfResponse.data.success && pdfResponse.data.pdfUrl) {
        setGeneratedPdfUrl(pdfResponse.data.pdfUrl);
      }
      
    } catch (error) {
      console.error("PDF generation failed:", error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to generate PDF';
      alert(`Error: ${errorMessage}`);
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleGenerateResume = async (e) => {
    e.preventDefault();
    
    // Check if user can generate (reusing subscription logic)
    if (!canGenerateQuestions()) {
      const usageInfo = getUsageInfo();
      alert(`You've reached your usage limit (${usageInfo?.used}/${usageInfo?.limit}). Please upgrade to continue.`);
      return;
    }

    // Move to step 4 for preview
    await nextStep();
  };

  const nextStep = async () => {
    // Save current data as original before moving to next step
    if (currentStep === 1) {
      setOriginalPersonalInfo({ ...personalInfo });
      setOriginalProfessionalSummary(professionalSummary);
    } else if (currentStep === 2) {
      setOriginalExperience(JSON.parse(JSON.stringify(experience)));
    } else if (currentStep === 3) {
      setOriginalEducation(JSON.parse(JSON.stringify(education)));
      setOriginalSkills(skills);
      
      // Generate PDF when moving to step 4
      await generatePdfPreview();
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleKeep = () => {
    // Keep the current data and move to next step
    nextStep();
  };

  const handleUndo = () => {
    // Revert to original data
    if (currentStep === 1) {
      setPersonalInfo({ ...originalPersonalInfo });
      setProfessionalSummary(originalProfessionalSummary);
    } else if (currentStep === 2) {
      setExperience(JSON.parse(JSON.stringify(originalExperience)));
    } else if (currentStep === 3) {
      setEducation(JSON.parse(JSON.stringify(originalEducation)));
      setSkills(originalSkills);
    }
  };

  // AI Assistance functions
  const getAiAssistance = async (fieldType, currentData, context = '') => {
    setAiLoading(true);
    setAiFieldType(fieldType);

    try {
      const token = await refreshTokenIfNeeded();

      if (!token) {
        alert('Please sign in to use AI assistance.');
        return;
      }

      const response = await axios.post(
        apiUrl('/api/resume-builder/ai-assist'),
        {
          fieldType,
          currentData,
          context,
          language
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        return response.data.suggestion;
      }
    } catch (error) {
      console.error('AI assistance error:', error);
      alert('Failed to get AI assistance. Please try again.');
    } finally {
      setAiLoading(false);
      setAiFieldType(null);
    }
  };

  const handleAiProfessionalSummary = async () => {
    const suggestion = await getAiAssistance('professional-summary', {
      personalInfo,
      experience: experience.filter(exp => exp.company || exp.position),
      education: education.filter(edu => edu.school || edu.degree),
      skills
    }, 'Write a concise 3-line ATS-compliant professional summary for a resume. Do not include the user\'s name or location. Focus on skills, adaptability, and value to employers.');

    if (suggestion) {
      setProfessionalSummary(suggestion);
      animateSummary(suggestion);
    }
  };

  const handleAiExperienceDescription = async (index) => {
    const exp = experience[index];
    const suggestion = await getAiAssistance('experience-description', exp, '');

    if (suggestion) {
      updateExperience(index, 'description', suggestion);
    }
  };

  const handleAiSkills = async () => {
    // Determine engineering field from education
    const engineeringField = education.find(edu => edu.field)?.field || 'engineering';
    const fieldContext = engineeringField.toLowerCase().includes('mechanical') ? 'mechanical engineering' :
                        engineeringField.toLowerCase().includes('civil') ? 'civil engineering' :
                        engineeringField.toLowerCase().includes('electrical') ? 'electrical engineering' :
                        engineeringField.toLowerCase().includes('chemical') ? 'chemical engineering' :
                        engineeringField.toLowerCase().includes('aerospace') ? 'aerospace engineering' :
                        engineeringField.toLowerCase().includes('biomedical') ? 'biomedical engineering' :
                        engineeringField.toLowerCase().includes('computer') ? 'computer engineering' :
                        'engineering';

    // Include language skills in the context
    const languageSkillsText = languageSkills
      .filter(lang => lang.language && lang.proficiency)
      .map(lang => `${lang.proficiency} in ${lang.language}`)
      .join(', ');

    const context = `Generate a comprehensive list of technical and soft skills for a ${fieldContext} professional. Include both technical skills specific to ${fieldContext} (like software tools, technical competencies, and industry-specific knowledge) and essential soft skills. ${languageSkillsText ? `Also include these language skills: ${languageSkillsText}.` : ''} Format as comma-separated values. Focus on skills that would be valuable in ${fieldContext} job applications.`;

    const suggestion = await getAiAssistance('skills', {
      skills,
      experience: experience.filter(exp => exp.company || exp.position),
      education: education.filter(edu => edu.school || edu.degree),
      languageSkills: languageSkills.filter(lang => lang.language && lang.proficiency)
    }, context);

    if (suggestion) {
      // Combine existing skills with AI suggestion, but preserve language skills
      const existingSkills = skills ? skills.split(',').map(s => s.trim()).filter(s => s) : [];
      const suggestedSkills = suggestion.split(',').map(s => s.trim()).filter(s => s);
      
      // Add language skills to the suggested skills
      const languageSkillStrings = languageSkills
        .filter(lang => lang.language && lang.proficiency)
        .map(lang => `${lang.proficiency} in ${lang.language}`);
      
      const combinedSkills = [...new Set([...existingSkills, ...suggestedSkills, ...languageSkillStrings])];
      setSkills(combinedSkills.join(', '));
    }
  };

  const handleResumeTypeSelection = (type) => {
    if (type === 'scratch') {
      // Check if user can create from scratch resume
      if (!canCreateScratchResume()) {
        const resumeUsage = getResumeUsageInfo();
        alert(`You've reached your from-scratch resume limit (${resumeUsage?.scratchUsed}/${resumeUsage?.scratchLimit || 1}). Please upgrade to create unlimited resumes!`);
        return;
      }
      // Start building from scratch
      setShowOnboarding(false);
    } else if (type === 'tailored') {
      // Navigate to Job Tailored Resume page directly with fresh navigation state
      navigate('/dashboard/job-tailored-resume', { state: { freshNavigation: true } });
    }
  };

  // Reset component when navigating back for fresh start
  useEffect(() => {
    // Check if this is a fresh navigation (coming from thank you page)
    const isFreshNavigation = !location.state?.preFilled && !location.state?.tailoredResume;
    
    if (isFreshNavigation) {
      // Reset all state for fresh start
      setCurrentStep(1);
      setShowOnboarding(true);
      setGeneratedPdfUrl(null);
      setPersonalInfo({
        fullName: "",
        email: "",
        phone: "",
        location: "",
        linkedIn: "",
        portfolio: ""
      });
      setProfessionalSummary("");
      setAnimatedProfessionalSummary("");
      setExperience([{
        type: 'work',
        company: "",
        position: "",
        startDate: "",
        endDate: "",
        current: false,
        description: "",
        projectName: "",
        techStack: "",
        projectLink: ""
      }]);
      setEducation([{
        school: "",
        degree: "",
        field: "",
        graduationYear: "",
        startYear: "",
        gpa: "",
        location: ""
      }]);
      setSkills("");
      setLanguageSkills([{ language: "", proficiency: "" }]);
      setAiLoading(false);
      setAiFieldType(null);
    }
  }, [location.pathname, location.state?.preFilled, location.state?.tailoredResume]);

  return (
    <div className="dashboard-container">
      <Sidebar activeItem="ai-resume-builder" />
      <div className="main-content">
        <div className="content-header">
          <h2>{preFilled ? 'Review Your Tailored Resume' : 'AI Resume Builder'}</h2>
          <p>
            {preFilled 
              ? 'Your resume has been optimized for ATS compatibility. Review and customize each section before generating your final PDF.'
              : 'Create a professional, ATS-friendly resume tailored to your target job.'
            }
          </p>
          {preFilled && jobAnalysis && (
            <div className="job-analysis-summary">
              <h4>🎯 Job Optimization Summary</h4>
              <div className="analysis-grid">
                <div className="analysis-item">
                  <span className="analysis-label">Target Role:</span>
                  <span className="analysis-value">{jobAnalysis.experienceLevel} {jobAnalysis.industry} position</span>
                </div>
                <div className="analysis-item">
                  <span className="analysis-label">Key Skills Matched:</span>
                  <span className="analysis-value">{jobAnalysis.requiredSkills?.slice(0, 3).join(', ')}</span>
                </div>
                <div className="analysis-item">
                  <span className="analysis-label">ATS Keywords:</span>
                  <span className="analysis-value">{jobAnalysis.keywords?.length} incorporated</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Onboarding Modal - Only show for fresh navigation without pre-filled data */}
        {showOnboarding && !preFilled && (
          <div className="onboarding-overlay">
            <div className="onboarding-modal">
              <h2 className="onboarding-title">How would you like to create your resume?</h2>
              
              <div className="onboarding-options">
                <div 
                  className="onboarding-option"
                  onClick={() => handleResumeTypeSelection('scratch')}
                >
                  <div className="option-header">
                    <h3>From Scratch</h3>
                  </div>
                  <p className="option-description">
                    Build your resume step-by-step with AI assistance. Perfect for creating a comprehensive, 
                    professional resume that highlights all your achievements.
                  </p>
                </div>

                <div 
                  className="onboarding-option"
                  onClick={() => handleResumeTypeSelection('tailored')}
                >
                  <div className="option-header">
                    <h3>Job Tailored Resume</h3>
                    <span className="recommended-badge">Recommended</span>
                  </div>
                  <p className="option-description">
                    Optimize your resume for a specific job posting. Our AI analyzes the job description 
                    and tailors your resume to match key requirements and keywords.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="resume-builder-progress">
          <div className={`progress-step ${currentStep > 1 ? 'completed' : currentStep === 1 ? 'active' : ''}`}>
            <div className="step-circle">
              {currentStep > 1 ? '✓' : '1'}
            </div>
            <span>Personal Info</span>
          </div>
          <div className={`progress-step ${currentStep > 2 ? 'completed' : currentStep === 2 ? 'active' : ''}`}>
            <div className="step-circle">
              {currentStep > 2 ? '✓' : '2'}
            </div>
            <span>Experience</span>
          </div>
          <div className={`progress-step ${currentStep > 3 ? 'completed' : currentStep === 3 ? 'active' : ''}`}>
            <div className="step-circle">
              {currentStep > 3 ? '✓' : '3'}
            </div>
            <span>Education & Skills</span>
          </div>
          <div className={`progress-step ${currentStep === 4 ? 'active' : ''}`}>
            <div className="step-circle">4</div>
            <span>Review & Download</span>
          </div>
        </div>

        <div className="form-container resume-form-container">
          <form onSubmit={handleGenerateResume}>
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
                <div className="resume-step">
                  <h3>Personal Information</h3>
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={personalInfo.fullName}
                      onChange={(e) => handlePersonalInfoChange('fullName', e.target.value)}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={personalInfo.email}
                        onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                        placeholder="john.doe@example.com"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={personalInfo.phone}
                        onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={personalInfo.location}
                      onChange={(e) => handlePersonalInfoChange('location', e.target.value)}
                      placeholder="San Francisco, CA"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>LinkedIn Profile</label>
                      <input
                        type="url"
                        value={personalInfo.linkedIn}
                        onChange={(e) => handlePersonalInfoChange('linkedIn', e.target.value)}
                        placeholder="https://linkedin.com/in/johndoe"
                      />
                    </div>
                    <div className="form-group">
                      <label>Portfolio/Website</label>
                      <input
                        type="url"
                        value={personalInfo.portfolio}
                        onChange={(e) => handlePersonalInfoChange('portfolio', e.target.value)}
                        placeholder="https://johndoe.com"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Professional Summary</label>
                    <div className="field-with-ai">
                      <textarea
                        value={animatedProfessionalSummary || professionalSummary}
                        onChange={(e) => {
                          setProfessionalSummary(e.target.value);
                          setAnimatedProfessionalSummary("");
                        }}
                        placeholder="Brief overview of your professional background and career objectives..."
                        rows="4"
                      />
                      <button 
                        type="button" 
                        className="ai-assist-btn"
                        onClick={handleAiProfessionalSummary}
                        disabled={aiLoading}
                      >
                        {aiLoading && aiFieldType === 'professional-summary' ? (
                          <>
                            <span className="ai-spinner">⟳</span> Generating...
                          </>
                        ) : (
                          <>
                            <span className="ai-icon">✨</span> AI Write
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Keep/Undo buttons for Step 1 - Only show if pre-filled from Job Tailored */}
                  {preFilled && (
                    <div className="action-buttons">
                      <span className="action-message">
                        {tailoredResume 
                          ? "✨ LeetEngineer optimized your contact info for ATS compatibility. Ready to proceed?"
                          : "✨ LeetEngineer crafted your personal info. Look good?"
                        }
                      </span>
                      <div className="action-buttons-group">
                        <button type="button" className="keep-btn" onClick={handleKeep}>
                          <span className="btn-icon">✓</span> Keep
                        </button>
                        <button type="button" className="undo-btn" onClick={handleUndo}>
                          <span className="btn-icon">↶</span> Undo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Experience & Projects */}
              {currentStep === 2 && (
                <div className="resume-step">
                  <h3>Experience & Projects</h3>
                  {experience.map((exp, index) => (
                    <div key={index} className="experience-entry">
                      <div className="entry-header">
                        <h4>Type</h4>
                        {experience.length > 1 && (
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeExperience(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      {/* Type Selector */}
                      <div className="form-group type-selector-group">
                        <div className="type-selector">
                          <label className="radio-label">
                            <input
                              type="radio"
                              name={`type-${index}`}
                              value="work"
                              checked={exp.type === 'work'}
                              onChange={(e) => updateExperience(index, 'type', e.target.value)}
                            />
                            <span>Work Experience</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              name={`type-${index}`}
                              value="project"
                              checked={exp.type === 'project'}
                              onChange={(e) => updateExperience(index, 'type', e.target.value)}
                            />
                            <span>Personal Project</span>
                          </label>
                        </div>
                      </div>

                      {exp.type === 'work' ? (
                        <>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Company</label>
                              <input
                                type="text"
                                value={exp.company}
                                onChange={(e) => updateExperience(index, 'company', e.target.value)}
                                placeholder="Company Name"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Position</label>
                              <input
                                type="text"
                                value={exp.position}
                                onChange={(e) => updateExperience(index, 'position', e.target.value)}
                                placeholder="Job Title"
                                required
                              />
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Start Date</label>
                              <input
                                type="month"
                                value={exp.startDate}
                                onChange={(e) => updateExperience(index, 'startDate', e.target.value)}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>End Date</label>
                              <input
                                type="month"
                                value={exp.endDate}
                                onChange={(e) => updateExperience(index, 'endDate', e.target.value)}
                                disabled={exp.current}
                                required={!exp.current}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={exp.current}
                                onChange={(e) => {
                                  updateExperience(index, 'current', e.target.checked);
                                  if (e.target.checked) {
                                    updateExperience(index, 'endDate', '');
                                  }
                                }}
                              />
                              <span>I currently work here</span>
                            </label>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Project Name</label>
                              <input
                                type="text"
                                value={exp.projectName}
                                onChange={(e) => updateExperience(index, 'projectName', e.target.value)}
                                placeholder="Project Name"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Tools & Technologies</label>
                              <input
                                type="text"
                                value={exp.techStack}
                                onChange={(e) => updateExperience(index, 'techStack', e.target.value)}
                                placeholder="AutoCAD, SolidWorks, MATLAB, PLC Programming..."
                              />
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Start Date</label>
                              <input
                                type="month"
                                value={exp.startDate}
                                onChange={(e) => updateExperience(index, 'startDate', e.target.value)}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>End Date</label>
                              <input
                                type="month"
                                value={exp.endDate}
                                onChange={(e) => updateExperience(index, 'endDate', e.target.value)}
                                disabled={exp.current}
                                required={!exp.current}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={exp.current}
                                onChange={(e) => {
                                  updateExperience(index, 'current', e.target.checked);
                                  if (e.target.checked) {
                                    updateExperience(index, 'endDate', '');
                                  }
                                }}
                              />
                              <span>This is an ongoing project</span>
                            </label>
                          </div>
                          <div className="form-group">
                            <label>Project Link</label>
                            <input
                              type="url"
                              value={exp.projectLink}
                              onChange={(e) => updateExperience(index, 'projectLink', e.target.value)}
                              placeholder="https://github.com/username/project"
                            />
                          </div>
                        </>
                      )}

                      <div className="form-group">
                        <label>Description</label>
                        <div className="field-with-ai">
                          <textarea
                            value={exp.description}
                            onChange={(e) => updateExperience(index, 'description', e.target.value)}
                            placeholder={exp.type === 'work' ? "Describe your responsibilities, achievements, and key projects..." : "Describe the project, your role, tools/technologies used, and outcomes..."}
                            rows="4"
                            required
                          />
                          <button 
                            type="button" 
                            className="ai-assist-btn"
                            onClick={() => handleAiExperienceDescription(index)}
                            disabled={aiLoading || (exp.type === 'work' ? (!exp.position || !exp.company) : !exp.projectName)}
                            title={exp.type === 'work' ? (!exp.position || !exp.company ? "Fill in position and company first" : "Get AI suggestions") : (!exp.projectName ? "Fill in project name first" : "Get AI suggestions")}
                          >
                            {aiLoading && aiFieldType === 'experience-description' ? (
                              <>
                                <span className="ai-spinner">⟳</span> Generating...
                              </>
                            ) : (
                              <>
                                <span className="ai-icon">✨</span> AI Write
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="add-btn" onClick={addExperience}>
                    + Add Experience
                  </button>

                  {/* Keep/Undo buttons for Step 2 - Only show if pre-filled from Job Tailored */}
                  {preFilled && (
                    <div className="action-buttons">
                      <span className="action-message">
                        {tailoredResume 
                          ? "🚀 LeetEngineer restructured your experience to highlight job-relevant achievements. Perfect for ATS?"
                          : "🚀 LeetEngineer highlighted your experience. Ready to impress?"
                        }
                      </span>
                      <div className="action-buttons-group">
                        <button type="button" className="keep-btn" onClick={handleKeep}>
                          <span className="btn-icon">✓</span> Keep
                        </button>
                        <button type="button" className="undo-btn" onClick={handleUndo}>
                          <span className="btn-icon">↶</span> Undo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Education & Skills */}
              {currentStep === 3 && (
                <div className="resume-step">
                  <h3>Education</h3>
                  {education.map((edu, index) => (
                    <div key={index} className="education-entry">
                      <div className="entry-header">
                        <h4>Education {index + 1}</h4>
                        {education.length > 1 && (
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeEducation(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>School/University</label>
                          <input
                            type="text"
                            value={edu.school}
                            onChange={(e) => updateEducation(index, 'school', e.target.value)}
                            placeholder="University Name"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Degree</label>
                          <input
                            type="text"
                            value={edu.degree}
                            onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                            placeholder="Bachelor of Science"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Field of Study</label>
                          <input
                            type="text"
                            value={edu.field}
                            onChange={(e) => updateEducation(index, 'field', e.target.value)}
                            placeholder="Mechanical Engineering"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Graduation Year</label>
                          <input
                            type="month"
                            value={edu.graduationYear}
                            onChange={(e) => updateEducation(index, 'graduationYear', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Start Year</label>
                            <input
                              type="month"
                              value={edu.startYear}
                              onChange={(e) => updateEducation(index, 'startYear', e.target.value)}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>GPA</label>
                            <input
                              type="text"
                              value={edu.gpa}
                              onChange={(e) => updateEducation(index, 'gpa', e.target.value)}
                              placeholder="3.62/4.0"
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Location (State, Country)</label>
                            <input
                              type="text"
                              value={edu.location}
                              onChange={(e) => updateEducation(index, 'location', e.target.value)}
                              placeholder="Clemson, United States"
                            />
                          </div>
                        </div>
                    </div>
                  ))}
                  <button type="button" className="add-btn" onClick={addEducation}>
                    + Add Education
                  </button>

                  <h3 style={{ marginTop: '30px' }}>Skills</h3>
                  <div className="form-group">
                    <label>Technical & Soft Skills</label>
                    <div className="field-with-ai">
                      <textarea
                        value={skills}
                        onChange={(e) => setSkills(e.target.value)}
                        placeholder="List your skills separated by commas (e.g., AutoCAD, SolidWorks, MATLAB, Project Management, Finite Element Analysis, etc.)"
                        rows="4"
                        required
                      />
                      <button 
                        type="button" 
                        className="ai-assist-btn"
                        onClick={handleAiSkills}
                        disabled={aiLoading}
                      >
                        {aiLoading && aiFieldType === 'skills' ? (
                          <>
                            <span className="ai-spinner">⟳</span> Generating...
                          </>
                        ) : (
                          <>
                            <span className="ai-icon">✨</span> AI Suggest
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Resume Language</label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      required
                    >
                      <option value="english">English</option>
                      <option value="spanish">Spanish</option>
                      <option value="french">French</option>
                      <option value="german">German</option>
                      <option value="italian">Italian</option>
                      <option value="portuguese">Portuguese</option>
                      <option value="dutch">Dutch</option>
                      <option value="russian">Russian</option>
                      <option value="chinese">Chinese</option>
                      <option value="japanese">Japanese</option>
                      <option value="korean">Korean</option>
                      <option value="arabic">Arabic</option>
                    </select>
                  </div>

                  <h3 style={{ marginTop: '30px' }}>Language Skills</h3>
                  <div className="form-group">
                    <label>Specify your language proficiencies (optional)</label>
                    <div className="language-skills-container">
                      {languageSkills.map((langSkill, index) => (
                        <div key={index} className="language-skill-entry">
                          <div className="language-input-group">
                            <select
                              value={langSkill.language}
                              onChange={(e) => updateLanguageSkill(index, 'language', e.target.value)}
                              className="language-select"
                            >
                              <option value="">Select Language</option>
                              <option value="english">English</option>
                              <option value="spanish">Spanish</option>
                              <option value="french">French</option>
                              <option value="german">German</option>
                              <option value="italian">Italian</option>
                              <option value="portuguese">Portuguese</option>
                              <option value="dutch">Dutch</option>
                              <option value="russian">Russian</option>
                              <option value="chinese">Chinese</option>
                              <option value="japanese">Japanese</option>
                              <option value="korean">Korean</option>
                              <option value="arabic">Arabic</option>
                              <option value="hindi">Hindi</option>
                              <option value="bengali">Bengali</option>
                              <option value="turkish">Turkish</option>
                              <option value="vietnamese">Vietnamese</option>
                              <option value="thai">Thai</option>
                              <option value="swedish">Swedish</option>
                              <option value="danish">Danish</option>
                              <option value="norwegian">Norwegian</option>
                              <option value="finnish">Finnish</option>
                            </select>
                            <select
                              value={langSkill.proficiency}
                              onChange={(e) => updateLanguageSkill(index, 'proficiency', e.target.value)}
                              className="proficiency-select"
                            >
                              <option value="">Proficiency</option>
                              <option value="native">Native</option>
                              <option value="fluent">Fluent</option>
                              <option value="intermediate">Intermediate</option>
                              <option value="basic">Basic</option>
                            </select>
                            <button
                              type="button"
                              className="remove-language-btn"
                              onClick={() => removeLanguageSkill(index)}
                              disabled={languageSkills.length === 1}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                      <button type="button" className="add-language-btn" onClick={addLanguageSkill}>
                        + Add Language
                      </button>
                    </div>
                  </div>

                  {/* Keep/Undo buttons for Step 3 - Only show if pre-filled from Job Tailored */}
                  {preFilled && (
                    <div className="action-buttons">
                      <span className="action-message">
                        {tailoredResume 
                          ? "🎓 LeetEngineer prioritized your skills for maximum ATS impact. All set for your dream job?"
                          : "🎓 LeetEngineer organized your education & skills. All set?"
                        }
                      </span>
                      <div className="action-buttons-group">
                        <button type="button" className="keep-btn" onClick={handleKeep}>
                          <span className="btn-icon">✓</span> Keep
                        </button>
                        <button type="button" className="undo-btn" onClick={handleUndo}>
                          <span className="btn-icon">↶</span> Undo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Review & Download */}
              {currentStep === 4 && (
                <div className="resume-step">
                  <h3>🎉 Review & Download Your Resume</h3>
                  <p className="step-description">
                    Review your resume preview below. Download when you're ready to share it with employers!
                  </p>

                      {/* Live Preview Panel */}
                      <div className="preview-scroll-container">
                        <ResumePreview
                          personalInfo={personalInfo}
                          professionalSummary={professionalSummary}
                          experience={experience}
                          education={education}
                          skills={skills}
                          showDownloadButton={true}
                        />
                      </div>                  {/* Download Actions */}
                  <div className="download-actions" style={{marginTop: '20px'}}>
                    {pdfGenerating ? (
                      <div className="pdf-generating">
                        <div className="loading-spinner resume-spinner"></div>
                        <p className="loading-text-resume">Generating your professional resume PDF...</p>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="download-btn"
                        onClick={() => {
                          // Trigger html2pdf download from ResumePreview
                          const pdfBtn = document.querySelector('.download-pdf-btn');
                          if (pdfBtn) {
                            pdfBtn.click();
                            // Navigate to new resume page after download
                            setTimeout(() => {
                              navigate('/dashboard/ai-resume-builder');
                            }, 1000);
                          }
                        }}
                      >
                        📥 Download Resume PDF
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="resume-navigation">
                {currentStep > 1 && currentStep < 4 && (
                  <button type="button" className="nav-btn prev-btn" onClick={prevStep}>
                    Previous
                  </button>
                )}
                {currentStep < 3 ? (
                  <button type="button" className="nav-btn next-btn" onClick={nextStep}>
                    Next
                  </button>
                ) : currentStep === 3 ? (
                  <button type="submit" className="nav-btn next-btn generate-resume-btn">
                    Generate Resume
                  </button>
                ) : null}
              </div>
            </form>
        </div>
        <Feedback />
      </div>
      <div className="dashboard-footer">
        <Footer />
      </div>
    </div>
  );
}

export default AIResumeBuilder;
